import type { OverviewView, ProblemItem } from '@monitor/contracts';
import { Activity, AlertTriangle, AppWindow, Bell, Bug, CheckCircle2, ChevronRight, Coins, Cpu, HeartPulse, PlugZap, ServerCrash, Timer, TrendingUp, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useOverview } from '../api/hook.js';
import { MiniBars, Meter, Sparkline, TimeSeriesChart } from '../component/chart.js';
import { Cls, Delta, Dot, Empty, Env, Failure, Loading, Panel, ProviderMark, Sev, State } from '../component/ui.js';
import { providerColor, STATE_COLOR } from '../lib/color.js';
import { ago, change, day, int, money, moneyShort, ms, pct, short, timeShort } from '../lib/format.js';

const PROBLEM_ICON: Record<ProblemItem['kind'], ReactNode> = {
  application: <AppWindow size={16} />,
  dependency: <ServerCrash size={16} />,
  error: <Bug size={16} />,
  alert: <Bell size={16} />,
  cost: <Coins size={16} />,
  provider: <PlugZap size={16} />,
};

export function OverviewPage() {
  const overview = useOverview();
  return (
    <>
      {overview.isPending ? (
        <div className="page overview">
          <Loading rows={4} />
        </div>
      ) : overview.isError ? (
        <div className="page overview">
          <Failure error={overview.error} />
        </div>
      ) : (
        <Overview data={overview.data} />
      )}
    </>
  );
}

function Section({ title, icon, meta, action, children }: { title: string; icon: ReactNode; meta?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="ov-section">
      <header className="ov-section-head">
        <span className="ov-section-icon">{icon}</span>
        <h2>{title}</h2>
        {meta ? <span className="ov-section-meta">{meta}</span> : null}
        <span className="spacer" />
        {action}
      </header>
      {children}
    </section>
  );
}

function Kpi({ to, icon, label, value, foot, children }: { to: string; icon: ReactNode; label: string; value: ReactNode; foot?: ReactNode; children?: ReactNode }) {
  return (
    <Link to={to} className="kpi">
      <span className="kpi-label">
        {icon} {label}
      </span>
      <span className="kpi-value num">{value}</span>
      {foot ? <span className="kpi-foot">{foot}</span> : null}
      {children}
    </Link>
  );
}

function Overview({ data }: { data: OverviewView }) {
  const openAlert = Object.values(data.kpi.openAlert).reduce((a, b) => a + b, 0);
  const totalApp = Object.values(data.applicationState).reduce((a, b) => a + b, 0);
  const hourlyError = data.topErrorApplication.reduce((sum, item) => sum.map((value, index) => value + (item.hourly[index] ?? 0)), new Array<number>(24).fill(0));
  const provider = data.provider;
  const costDays = data.costTrend.map((item) => item.day);
  const anomalyIndex = data.costAnomaly
    .map((item) => ({
      index: costDays.indexOf(item.day),
      label: '⚠',
      color: 'var(--warning)',
    }))
    .filter((item) => item.index >= 0);
  const hasCost = data.costTrend.some((point) => Object.values(point.value).some((value) => value > 0));
  const budgetRatio = data.kpi.budgetMonth ? data.kpi.costForecast / data.kpi.budgetMonth : null;
  const live = data.application.filter((item) => item.state !== 'Unknown');
  const waiting = data.application.filter((item) => item.state === 'Unknown');
  const hasTraffic = data.kpi.requestLast24h > 0;
  const newError = data.errorHotspot.filter((item) => item.classification === 'New').length;
  const hotError = data.errorHotspot.filter((item) => item.classification === 'Spiking' || item.classification === 'Regression').length;
  const connected = data.providerState.Connected + data.providerState.Degraded;
  const requestChange = change(data.kpi.requestLast24h, data.kpi.requestPrevious24h);
  const errorChange = change(data.kpi.errorLast24h, data.kpi.errorPrevious24h);
  const costChange = change(data.kpi.costMonth, data.kpi.costPreviousMonth);
  const none = <small className="kpi-none">Nessun dato</small>;

  return (
    <div className="page overview">
      <section className={`banner ${data.state}`} aria-live="polite">
        <div className="banner-state">
          <span className="banner-icon">{data.state === 'Healthy' ? <CheckCircle2 size={22} /> : data.state === 'Degraded' ? <AlertTriangle size={22} /> : data.state === 'Unknown' ? <Timer size={22} /> : <XCircle size={22} />}</span>
          <div>
            <div className="banner-headline">{data.headline}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Aggiornato alle {timeShort(data.generatedAt)}: {totalApp} Application e {provider.length} Provider
            </div>
          </div>
        </div>
        <div className="banner-count">
          {(['Healthy', 'Degraded', 'Unhealthy', 'Unknown'] as const).map((state) => (
            <Link key={state} to="/health" className={data.applicationState[state] ? undefined : 'zero'}>
              <span className="banner-number">
                <Dot state={state} />
                {data.applicationState[state]}
              </span>
              <span className="banner-label">{state}</span>
            </Link>
          ))}
        </div>
      </section>

      <Section
        title="Salute"
        icon={<HeartPulse size={16} />}
        meta="Ultime 24 h"
        action={
          <Link className="panel-link" to="/application">
            Tutte le Application <ChevronRight size={13} />
          </Link>
        }
      >
        <div className="ov-kpi">
          <Kpi
            to="/usage"
            icon={<Activity size={13} />}
            label="Richieste"
            value={short(data.kpi.requestLast24h)}
            foot={
              requestChange !== null ? (
                <>
                  <Delta value={requestChange} upIsBad={false} /> rispetto alle 24 h prima
                </>
              ) : null
            }
          />
          <Kpi to="/health" icon={<Timer size={13} />} label="Tempo di risposta" value={hasTraffic ? ms(data.kpi.latencyP95) : none} foot={hasTraffic ? '95 richieste su 100 rispondono entro questo tempo' : null} />
          <Kpi to="/health" icon={<CheckCircle2 size={13} />} label="Disponibilità" value={hasTraffic ? pct(data.kpi.uptime24h, 2) : none} foot={hasTraffic ? `${live.length} Application attive su ${totalApp}` : null} />
        </div>

        <Panel title="Application" icon={<AppWindow size={15} />} meta={`${live.length} attive su ${data.application.length}`}>
          {data.application.length ? (
            <div className="tiles">
              {[...live, ...waiting].map((item) => (
                <Link
                  key={item.id}
                  to={`/application/${item.id}`}
                  className={`tile ${item.state}`}
                >
                  <div className="tile-head">
                    <Dot state={item.state} silent />
                    <span className="tile-name ellipsis">{item.name}</span>
                    {item.environment !== 'production' ? <Env value={item.environment} /> : null}
                    {item.openAlert ? (
                      <span className="nav-badge critical" style={{ marginLeft: 'auto' }}>
                        {item.openAlert}
                      </span>
                    ) : null}
                  </div>
                  {item.latencySeries.some((value) => value > 0) ? <Sparkline values={item.latencySeries} color={STATE_COLOR[item.state]} height={24} /> : null}
                  {item.state === 'Unknown' ? (
                    <div className="tile-metric">
                      <span>{item.lastCheckAt ? `Ultimo dato ${ago(item.lastCheckAt)}` : 'Mai collegata'}</span>
                    </div>
                  ) : (
                    <div className="tile-metric">
                      {item.latencyP95 > 0 ? <b>{ms(item.latencyP95)}</b> : null}
                      {item.errorRate > 0 ? <span className={item.errorRate >= 0.02 ? 'bad' : undefined}>{pct(item.errorRate)} errori</span> : <span>Nessun errore</span>}
                      {item.cpu !== null ? <span>CPU {pct(item.cpu, 0)}</span> : null}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <Empty icon={<Timer size={22} />}>Nessuna Application registrata.</Empty>
          )}
        </Panel>
      </Section>

      <Section
        title="Problemi"
        icon={<AlertTriangle size={16} />}
        meta={data.problem.length ? `${data.problem.length} da guardare` : 'Tutto tranquillo'}
        action={
          <Link className="panel-link" to="/alert">
            Gestisci gli Alert <ChevronRight size={13} />
          </Link>
        }
      >
        <div className="ov-kpi">
          <Kpi
            to="/error"
            icon={<Bug size={13} />}
            label="Error nelle ultime 24 h"
            value={int(data.kpi.errorLast24h)}
            foot={
              errorChange !== null ? (
                <>
                  <Delta value={errorChange} /> rispetto alle 24 h prima
                </>
              ) : null
            }
          >
            {data.kpi.errorLast24h ? (
              <div className="kpi-spark">
                <MiniBars values={hourlyError} width={220} height={26} />
              </div>
            ) : null}
          </Kpi>
          <Kpi
            to="/error"
            icon={<TrendingUp size={13} />}
            label="Error nuovi o in crescita"
            value={
              newError || hotError ? (
                <>
                  {newError}
                  <small>Nuovi</small> {hotError}
                  <small>In crescita</small>
                </>
              ) : (
                0
              )
            }
            foot={data.errorHotspot[0] ? `${data.errorHotspot[0].applicationName}: ${data.errorHotspot[0].type.split('.').at(-1)}` : null}
          />
          <Kpi
            to="/alert"
            icon={<Bell size={13} />}
            label="Alert aperti"
            value={openAlert}
            foot={
              openAlert ? (
                <span className="row wrap" style={{ gap: 4 }}>
                  {(['Critical', 'Error', 'Warning', 'Info'] as const).map((severity) =>
                    data.kpi.openAlert[severity] ? (
                      <span key={severity} className={`sev ${severity}`}>
                        {data.kpi.openAlert[severity]} {severity}
                      </span>
                    ) : null,
                  )}
                </span>
              ) : null
            }
          />
        </div>

        <div className="ov-row">
          <Panel title="Problemi adesso" icon={<AlertTriangle size={15} />} meta={data.problem.length ? `${data.problem.length}` : undefined} flush className="fill">
            {data.problem.length === 0 ? (
              <Empty icon={<CheckCircle2 size={22} />}>Nessun problema aperto.</Empty>
            ) : (
              <div className="ov-scroll">
                {data.problem.map((item) => (
                  <Link key={item.id} to={item.href} className="problem">
                    <span className={`problem-bar ${item.severity}`} />
                    <span className="problem-icon">{PROBLEM_ICON[item.kind]}</span>
                    <span style={{ minWidth: 0 }}>
                      <div className="problem-title">{item.title}</div>
                      <div className="problem-detail">{item.detail}</div>
                    </span>
                    <Sev value={item.severity} />
                  </Link>
                ))}
              </div>
            )}
          </Panel>
          <Panel
            title="Error da guardare"
            icon={<Bug size={15} />}
            flush
            className="fill"
            action={
              <Link className="panel-link" to="/error">
                Tutti <ChevronRight size={13} />
              </Link>
            }
          >
            {data.errorHotspot.length === 0 ? (
              <Empty icon={<CheckCircle2 size={22} />}>Nessun Error aperto.</Empty>
            ) : (
              <div className="ov-scroll">
                {data.errorHotspot.map((item) => (
                  <Link key={item.id} to={`/error/${item.id}`} className="problem plain">
                    <span style={{ minWidth: 0 }}>
                      <div className="problem-title mono" style={{ fontSize: 12 }}>
                        {item.type.split('.').at(-1)}
                      </div>
                      <div className="problem-detail">
                        {item.applicationName}: {item.count24h} in 24 h, ultimo {ago(item.lastSeen)}
                      </div>
                    </span>
                    <span className="row">
                      <MiniBars values={item.hourly} width={60} height={18} />
                      <Cls value={item.classification} />
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </Panel>
          <Panel title="Chi genera più Error" icon={<AppWindow size={15} />} meta="24 h" className="fill">
            {data.topErrorApplication.length === 0 ? (
              <Empty icon={<CheckCircle2 size={22} />}>Nessuna Application con Error.</Empty>
            ) : (
              <div className="barlist">
                {data.topErrorApplication.map((item) => (
                  <Link key={item.applicationId} to={`/error?application=${item.applicationId}`} className="barlist-row">
                    <div className="barlist-label ellipsis">{item.name}</div>
                    <div className="barlist-value row">
                      <Delta value={change(item.count, item.previous)} />
                      {int(item.count)}
                    </div>
                    <div className="barlist-track">
                      <i
                        style={{
                          width: `${(item.count / Math.max(1, data.topErrorApplication[0]!.count)) * 100}%`,
                          background: 'var(--serious)',
                        }}
                      />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </Section>

      <Section
        title="Costi"
        icon={<Coins size={16} />}
        meta="Mese in corso"
        action={
          <Link className="panel-link" to="/cost">
            Dettaglio dei costi <ChevronRight size={13} />
          </Link>
        }
      >
        <div className="ov-kpi">
          <Kpi
            to="/cost"
            icon={<Coins size={13} />}
            label="Speso nel mese"
            value={money(data.kpi.costMonth)}
            foot={
              costChange !== null ? (
                <>
                  <Delta value={costChange} /> rispetto al mese prima
                </>
              ) : null
            }
          />
          <Kpi to="/cost" icon={<TrendingUp size={13} />} label="Previsione a fine mese" value={money(data.kpi.costForecast)} foot={budgetRatio !== null ? `${pct(budgetRatio, 0)} del budget di ${moneyShort(data.kpi.budgetMonth)}` : null}>
            {data.kpi.budgetMonth ? (
              <div style={{ marginTop: 4 }}>
                <Meter value={data.kpi.costForecast} max={data.kpi.budgetMonth} marker={data.kpi.costMonth} />
              </div>
            ) : null}
          </Kpi>
          <Kpi
            to="/provider"
            icon={<PlugZap size={13} />}
            label="Provider collegati"
            value={`${connected}/${provider.length}`}
            foot={data.providerState.NotConfigured ? `${data.providerState.NotConfigured} da configurare` : data.providerState.Disconnected ? `${data.providerState.Disconnected} disconnessi` : 'Tutti collegati'}
          />
        </div>

        <Panel title="Costo giornaliero per Provider" icon={<Coins size={15} />} meta="Ultimi 30 giorni">
          {hasCost ? (
            <TimeSeriesChart
              x={costDays}
              kind="bar"
              stacked
              height={220}
              xFormat={day}
              format={(value) => moneyShort(value)}
              marker={anomalyIndex}
              series={provider.map((item) => ({
                key: item.id,
                label: item.name,
                color: providerColor(item.id),
                values: data.costTrend.map((point) => point.value[item.id] ?? 0),
              }))}
            />
          ) : (
            <Empty icon={<Coins size={22} />}>Nessun costo registrato negli ultimi 30 giorni.</Empty>
          )}
        </Panel>

        <div className={`ov-row ${data.topConsumer.length ? 'two' : 'one'}`}>
          <Panel
            title="Provider"
            icon={<PlugZap size={15} />}
            flush
            className="fill"
            action={
              <Link className="panel-link" to="/provider">
                Configura <ChevronRight size={13} />
              </Link>
            }
          >
            {provider.map((item) => (
              <Link key={item.id} to={`/provider?id=${item.id}`} className="ov-provider">
                <ProviderMark id={item.id} />
                <strong className="ellipsis">{item.name}</strong>
                {item.state === 'NotConfigured' ? <State value={item.state} /> : <span className="num">{money(item.costMonth)}</span>}
                {item.budgetMonth && item.state !== 'NotConfigured' ? <Meter value={item.costMonth} max={item.budgetMonth} /> : <span />}
              </Link>
            ))}
          </Panel>
          {data.topConsumer.length ? (
            <Panel title="Chi consuma di più" icon={<Cpu size={15} />} className="fill">
              <div className="barlist">
                {data.topConsumer.map((item) => (
                  <Link key={item.applicationId} to={`/application/${item.applicationId}`} className="barlist-row">
                    <div className="barlist-label ellipsis">{item.name}</div>
                    <div className="barlist-value">
                      {money(item.cost)}
                      {item.cpu !== null ? (
                        <span className="muted" style={{ fontWeight: 400 }}>
                          {' '}
                          (CPU {pct(item.cpu, 0)})
                        </span>
                      ) : null}
                    </div>
                    <div className="barlist-track">
                      <i
                        style={{
                          width: `${(item.cost / Math.max(0.01, data.topConsumer[0]!.cost)) * 100}%`,
                          background: 'var(--series-1)',
                        }}
                      />
                    </div>
                  </Link>
                ))}
              </div>
            </Panel>
          ) : null}
        </div>
      </Section>
    </div>
  );
}
